import { useEffect, useState } from "react";
import { useMounted } from "@app/hooks/useMounted";
import {
  getExcelReport,
  getExcelSubReport,
  getReportProp,
  getReportResult,
  getSubReportProp,
  getSubReportResult,
  ReportColumn,
  ReportFilter,
  ReportProps,
} from "@app/api/reports.api";
import { GlobalSpinner } from "@app/components/common/GlobalSpinner/GlobalSpinner";
import { themeObject } from "@app/styles/themes/themeVariables";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import ReportSearchResultComponent from "./ReportSearchResultComponent";
import { useNavigate, useParams } from "react-router-dom";
import { IList } from "@app/entities/IListResponse";
import { BaseModal } from "@app/components/common/BaseModal/BaseModal";
import { useTranslation } from "react-i18next";
import { BaseActionsWrapper } from "@app/components/common/BaseActionsWrapper/BaseActionsWrapper";

export const ReportViewer: React.FC = () => {
  const { t } = useTranslation();
  const { isMounted } = useMounted();
  const [isGeneratingReport, setGeneratingReport] = useState(false);
  const { id, systemName } = useParams();
  const [report, setReport] = useState<ReportProps>();
  const [reportColumns, setReportColumns] = useState<ReportColumn[]>([]);
  const [reportFilters, setReportFilters] = useState<ReportFilter[]>([]);

  const [isGeneratingSubReport, setGeneratingSubReport] = useState(false);
  const [subReport, setSubReport] = useState<ReportProps>();
  const [subReportColumns, setSubReportColumns] = useState<ReportColumn[]>([]);
  const [subReportFilters, setSubReportFilters] = useState<ReportFilter[]>([]);
  const [subReportRoute, setSubReportRoute] = useState("");
  const [openSubReportModal, setOpenSubReportModal] = useState(false);
  const navigate = useNavigate();

  const getReport = async (categoryId: number, systemName: string) => {
    setGeneratingReport(true);
    try {
      await getReportProp(categoryId, systemName)
        .then((res) => {
          if (isMounted.current) {
            if (res && res.data) {
              setReport(res.data);
              setReportFilters(res.data.reportFilters);
              setReportColumns(res.data.reportColumns);
            } else {
              navigate("/");
            }
          }
        })
        .finally(() => setGeneratingReport(false));
    } catch (error) {
      navigate("/");
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getReport(parseInt(id?.toString() || "0"), systemName?.toString() || "");
  }, [isMounted]);

  const searchReport = async (
    filterBuilder: any,
    pageSize: number,
    current: number
  ): Promise<IList<any>> => {
 
    try {
      const result = await getReportResult(
        parseInt(id?.toString() || "0"),
        systemName?.toString() || "",
        filterBuilder,
        pageSize,
        current
      );
      if (result && isMounted.current) {
        return result.data;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    return {
      data: [],
      currentPageNumber: 0,
      pageSize: 0,
      totalRecords: 0,
    };
  };

  const downloadReport = async (filterBuilder: any): Promise<any> => {
    const filters = filterBuilder;

    try {
      const result = await getExcelReport(
        parseInt(id?.toString() || "0"),
        systemName?.toString() || "",
        filterBuilder
      );

      if (result && isMounted.current) {
        return result.data;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    return {};
  };

  const openSubReport = async (route: string) => {
    setOpenSubReportModal(true);
    await getSubReport(route);
  };

  const closeSubReport = () => {
    setOpenSubReportModal(false);
  };

  const getSubReport = async (route: string) => {
    setGeneratingSubReport(true);
    setSubReportRoute(route);
    try {
      await getSubReportProp(route)
        .then((res) => {
          if (isMounted.current) {
            if (res && res.data) {
              setSubReport(res.data);
              setSubReportFilters(res.data.reportFilters);
              setSubReportColumns(res.data.reportColumns);
            } else {
              setOpenSubReportModal(false);
            }
          }
        })
        .finally(() => setGeneratingSubReport(false));
    } catch (error) {
      //setOpenSubReportModal(false);
      console.error("Error fetching data:", error);
    }
  };

  const searchSubReport = async (
    filterBuilder: any,
    pageSize: number,
    current: number
  ): Promise<IList<any>> => {
    try {
      const result = await getSubReportResult(
        subReportRoute,
        filterBuilder,
        pageSize,
        current
      );
      if (result && isMounted.current) {
        return result.data;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      //setOpenSubReportModal(false);
    }

    return {
      data: [],
      currentPageNumber: 0,
      pageSize: 0,
      totalRecords: 0,
    };
  };

  const downloadSubReport = async (filterBuilder: any): Promise<any> => {
    const filters = filterBuilder;
    try {
      const result = await getExcelSubReport(subReportRoute, filters);

      if (result && isMounted.current) {
        return result.data;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      //setOpenSubReportModal(false);
    }

    return {};
  };

  return (
    <>
      {isGeneratingReport ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <GlobalSpinner color={themeObject["dark"].spinnerBase} />
        </div>
      ) : (
        <>
          <ReportSearchResultComponent
            systemName={systemName!}
            reportProps={report!}
            reportFiltersProps={reportFilters}
            reportColumnsProps={reportColumns}
            onSearch={searchReport}
            onDownload={downloadReport}
            openSubReport={openSubReport}
            hideTitle={false}
          />
          <BaseModal
            open={openSubReportModal}
            destroyOnClose={true}
            onCancel={closeSubReport}
            title={subReport?.name}
            width={"80%"}
            footer={[
              <BaseActionsWrapper justify="end">
                <BaseButton key="cancel" onClick={closeSubReport}>
                  {t("common.close")}
                </BaseButton>
              </BaseActionsWrapper>,
            ]}
          >
            {isGeneratingSubReport ? (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 1000,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <GlobalSpinner color={themeObject["dark"].spinnerBase} />
              </div>
            ) : (
              <>
                <ReportSearchResultComponent
                  hideTitle={true}
                  systemName={systemName!}
                  reportProps={subReport!}
                  reportFiltersProps={subReportFilters}
                  reportColumnsProps={subReportColumns}
                  onSearch={searchSubReport}
                  onDownload={downloadSubReport}
                  closeSubReport={closeSubReport}
                />
              </>
            )}
          </BaseModal>
        </>
      )}
    </>
  );
};
